import { APIDetails } from './apis-details';

export const careerAdviceApi: APIDetails = {
  id: 'API_KEYS.careerAdviceApi',
  title: 'Career Content & Advice API',
  subtitle:
    'AI-powered company and employee profile details for career simulation and guidance.',
  description: [
    'HR tech platforms and learning environments need realistic employee and company profiles to help people explore careers and make confident decisions. Creating these profiles and personalized career guidance manually is slow, inconsistent, and difficult to scale—often resulting in generic advice that fails to engage users.',
    'The Career Content & Advice API brings career exploration to life with intelligent, personalized guidance. It delivers realistic employee profiles, company scenarios, and tailored career suggestions that help users visualize real-world career paths. Employees and learners receive ongoing recommendations for skills, certifications, and courses—keeping them motivated and aligned with their career goals.',
    'Built for growth and flexibility, the API enables platforms to deliver consistent, high-quality career guidance at scale. It’s an ideal solution for learning platforms, HR tech startups, and career simulation tools looking to enhance user engagement, improve decision-making, and deliver meaningful career outcomes.',
  ],
  overview: [
    'The Career Content & Advice API enables platforms to generate realistic company and employee (NPC) profiles and deliver AI-powered career guidance at scale. It provides structured endpoints to manage company data, employee profiles, and personalized career suggestions powered by LLMs.',
    '',
    'Using this API, systems can:',
    '• Create and manage company profiles with industry context, goals, and projects',
    '• Create and manage employee (NPC) profiles with education and work history',
    '• Generate personalized career suggestions, skill recommendations, and learning paths',
    '• Capture and update feedback on AI-generated guidance',
    '• Support career simulation, workforce planning, and continuous professional development',
    '',
    'Designed for flexibility and scalability, the API integrates seamlessly with HR tech platforms, EdTech products, career simulation tools, and learning environments to deliver consistent, high-quality career guidance experiences.',
  ],
  features: [
    {
      title: 'Company Profile',
      description:
        'Create, update, retrieve, and delete company profiles including projects, goals, and industry information.',
    },
    {
      title: 'Employee Profile',
      description:
        'Create, update, retrieve, and delete employee profiles with profession, education, and work history.',
    },
    {
      title: 'Career Suggestions & Feedback',
      description:
        'Generate AI-powered career guidance and suggestions based on employee profiles and company context. Give feedback on suggestions.',
    },
  ],
  workflow: [
    'Admin creates a company profile',
    'Admin or system creates employee profiles associated with the company',
    'System generates AI-powered career suggestions for each employee using LLM',
    'Users or admins retrieve career suggestions and provide feedback',
    'Feedback can be updated, and employee or company profiles can be archived or deleted',
  ],
  endpoints: [
    {
      category: 'Company Management',
      items: [
        {
          category: 'Create Company Profile',
          method: 'POST',
          path: '/create_company_profile',
          description: 'Creates a new company profile.',
        },
        {
          category: 'Update Company Profile',
          method: 'PUT',
          path: '/update_company_profile/{company_id}',
          description: 'Updates existing company profile.',
        },
        {
          category: 'Delete Company Profile',
          method: 'DELETE',
          path: '/delete_company_profile/{company_id}',
          description: 'Deletes a company profile permanently.',
        },
        {
          category: 'Get Company Profile',
          method: 'GET',
          path: '/get_company_profile/{company_id}',
          description: 'Retrieves complete company profile.',
        },
      ],
    },
    {
      category: 'Employee (NPC) Management',
      items: [
        {
          category: 'Create NPC Profile',
          method: 'POST',
          path: '/create_npc_profile',
          description: 'Creates employee profiles associated with a company.',
        },
        {
          category: 'Update NPC Profile',
          method: 'PUT',
          path: '/update_npc_profile/{npc_id}',
          description: 'Updates existing employee profile.',
        },
        {
          category: 'Delete NPC Profile',
          method: 'DELETE',
          path: '/delete_npc_profile/{npc_id}',
          description: 'Deletes employee profile permanently.',
        },
        {
          category: 'Get NPC Profile',
          method: 'GET',
          path: '/get_npc_profile/{npc_id}',
          description: 'Retrieves complete employee profile.',
        },
        {
          category: 'Get NPC Profiles by Company',
          method: 'GET',
          path: '/get_npc_profiles/{company_id}',
          description:
            'Retrieves all employee profiles for a specific company.',
        },
        {
          category: 'Update NPC Feedback',
          method: 'PUT',
          path: '/feedback/{suggestion_id}',
          description: 'Update feedback for generated suggestions.',
        },
        {
          category: 'Get NPC Suggestions',
          method: 'GET',
          path: '/get_npc_suggestions/{npc_id}',
          description: 'Retrieves suggestions for a specific employee.',
        },
      ],
    },
  ],
  apiDocumentation: [
    // Company Profile APIs
    {
      title: 'Create Company Profile',
      method: 'POST',
      path: '/create_company_profile',
      requestPath: 'POST /create_company_profile',
      description: 'Creates a new company profile.',
      requestExample: {
        name: 'UpscaleBD Ltd.',
        industry: 'Tech',
        description:
          'We partner with businesses to drive growth and foster innovation.',
        goals:
          'Empower businesses to achieve full potential and sustainable growth.',
        projects: ['HrX', 'SalseX', 'AI School'],
      },
      responseExample: {
        success: true,
        message: 'Company profile created successfully',
        company_id: 1,
      },
      statusCode: '200',
    },
    {
      title: 'Update Company Profile',
      method: 'PUT',
      path: '/update_company_profile/{company_id}',
      requestPath: 'PUT /update_company_profile/{company_id}',
      description: 'Updates existing company profile.',
      requestExample: {
        name: 'UpscaleBD Ltd. 2',
        industry: 'Tech',
        description: 'Updated description for company.',
        goals: 'Updated company goals.',
        projects: ['HrX', 'SalseX', 'AI School'],
      },
      responseExample: {
        success: true,
        message: 'Company profile updated successfully',
        company_id: 1,
      },
      statusCode: '200',
    },
    {
      title: 'Delete Company Profile',
      method: 'DELETE',
      path: '/delete_company_profile/{company_id}',
      requestPath: 'DELETE /delete_company_profile/{company_id}',
      description: 'Deletes a company profile permanently.',
      responseExample: {
        success: true,
        message: 'Company profile 1 deleted successfully',
        company_id: 1,
      },
      statusCode: '200',
    },
    {
      title: 'Get Company Profile by ID',
      method: 'GET',
      path: '/get_company_profile/{company_id}',
      requestPath: 'GET /get_company_profile/{company_id}',
      description: 'Retrieves complete company profile.',
      responseExample: {
        name: 'UpscaleBD Ltd.',
        industry: 'Tech',
        description:
          'We partner with businesses to drive growth and foster innovation.',
        goals:
          'Empower businesses to achieve full potential and sustainable growth.',
        projects: ['HrX', 'SalseX', 'AI School'],
      },
      statusCode: '200',
    },

    // NPC Profile APIs
    {
      title: 'Create NPC Profile',
      method: 'POST',
      path: '/create_npc_profile',
      requestPath: 'POST /create_npc_profile',
      description: 'Creates employee profiles associated with a company.',
      requestExample: {
        profession: 'Junior Software Engineer',
        company_id: 1,
        education_history: [
          {
            degree: 'BSc',
            institution: 'DIU',
            year: 2025,
            description: 'Computer Science major',
          },
        ],
        work_history: [
          {
            role: 'Intern',
            company: 'UpscaleBD Ltd.',
            duration: '3.5 months',
            description:
              'Built a data pipeline and started working on AI features.',
          },
        ],
      },
      responseExample: {
        success: true,
        message: 'NPC profile created successfully',
        npc_id: 3,
      },
      statusCode: '200',
    },
    {
      title: 'Update NPC Profile',
      method: 'PUT',
      path: '/update_npc_profile/{npc_id}',
      requestPath: 'PUT /update_npc_profile/{npc_id}',
      description: 'Updates existing employee profile.',
      requestExample: {
        profession: 'Junior Software Engineer',
        education_history: [
          {
            degree: 'BSc',
            institution: 'DIU',
            year: 2025,
            description: '',
          },
        ],
        work_history: [
          {
            role: 'Intern',
            company: 'UpscaleBD Ltd.',
            duration: '3.5 months',
            description: 'Updated work experience.',
          },
        ],
      },
      responseExample: {
        success: true,
        message: 'NPC profile updated successfully',
        npc_id: 3,
      },
      statusCode: '200',
    },
    {
      title: 'Delete NPC Profile',
      method: 'DELETE',
      path: '/delete_npc_profile/{npc_id}',
      requestPath: 'DELETE /delete_npc_profile/{npc_id}',
      description: 'Deletes employee profile permanently.',
      responseExample: {
        success: true,
        message: 'NPC profile 3 deleted successfully',
        npc_id: 3,
      },
      statusCode: '200',
    },
    {
      title: 'Get NPC Profile by ID',
      method: 'GET',
      path: '/get_npc_profile/{npc_id}',
      requestPath: 'GET /get_npc_profile/{npc_id}',
      description: 'Retrieves complete employee profile.',
      responseExample: {
        profession: 'Junior Software Engineer',
        education_history: [
          {
            degree: 'BSc',
            institution: 'DIU',
            year: 2025,
            description: '',
          },
        ],
        work_history: [
          {
            role: 'Intern',
            company: 'UpscaleBD Ltd.',
            duration: '3.5 months',
            description: 'Worked on AI features.',
          },
        ],
      },
      statusCode: '200',
    },
    {
      title: 'Get NPC Profiles by Company ID',
      method: 'GET',
      path: '/get_npc_profiles/{company_id}',
      requestPath: 'GET /get_npc_profiles/{company_id}',
      description: 'Retrieves all employee profiles for a specific company.',
      responseExample: [
        {
          profession: 'Junior Software Engineer',
          education_history: [
            { degree: 'BSc', institution: 'DIU', year: 2025 },
          ],
          work_history: [
            {
              role: 'Intern',
              company: 'UpscaleBD Ltd.',
              duration: '3.5 months',
            },
          ],
        },
      ],
      statusCode: '200',
    },

    // NPC Suggestions APIs
    {
      title: 'Update NPC Feedback',
      method: 'PUT',
      path: '/feedback/{suggestion_id}?feedback=good',
      requestPath: 'PUT /feedback/{suggestion_id}?feedback=good',
      description: 'Update feedback for generated suggestions.',
      responseExample: {
        success: true,
        message: 'NPC suggestion feedback updated successfully',
        suggestion_id: 7,
      },
      statusCode: '200',
    },
    {
      title: 'Get NPC Suggestions',
      method: 'GET',
      path: '/get_npc_suggestions/{npc_id}',
      requestPath: 'GET /get_npc_suggestions/{npc_id}',
      description: 'Retrieves suggestions for a specific employee.',
      responseExample: [
        {
          feedback: 'good',
          suggestions: [
            {
              type: 'course',
              title: 'Generative AI with LLMs',
              provider: 'Coursera',
              link: 'https://www.coursera.org/learn/generative-aiwith-llms',
              outline: [
                'Fundamentals of LLMs',
                'Prompt Engineering',
                'Deploying AI apps',
              ],
              expectation:
                'Equip with foundational AI skills for UpscaleBD projects.',
            },
          ],
          created_at: '2025-10-31T11:20:23.545275',
        },
      ],
      statusCode: '200',
    },
  ],
  useCases: [
    {
      title: 'Career Exploration Platforms',
      description:
        'Help users visualize real-world career paths through realistic employee and company scenarios.',
    },
    {
      title: 'Employee Growth & Development',
      description:
        'Provide personalized career guidance, skill recommendations, and learning paths to support long-term employee growth.',
    },
    {
      title: 'HR & Talent Development Systems',
      description:
        'Enable internal career planning and role progression insights to improve employee engagement and retention.',
    },
    {
      title: 'Learning & EdTech Platforms',
      description:
        'Deliver personalized career advice alongside course and certification recommendations to increase learner success.',
    },
    {
      title: 'Career Simulation Tools',
      description:
        'Create immersive, scenario-based career environments for informed decision-making and professional development.',
    },
    {
      title: 'University & Training Portals',
      description:
        'Guide students with structured career suggestions aligned with industries and job roles.',
    },
    {
      title: 'Coaching & Mentorship Platforms',
      description:
        'Scale personalized career coaching while maintaining consistent, high-quality guidance.',
    },
    {
      title: 'Workforce Planning & Upskilling',
      description:
        'Help organizations identify skill gaps and assess future role readiness across teams.',
    },
  ],
  targetUsers: [
    'HR & Career Professionals: Manage company and employee profiles, and plan employee development.',
    'EdTech Platforms & Learning Apps: Deliver AI-driven career guidance and course recommendations.',
    'Students & Working Professionals: Receive personalized career suggestions and learning paths.',
    'Companies & Startups: Simulate workforce planning, upskilling, and training using employee profiles.',
  ],
};
